import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Network,
  CalendarClock,
  Calendar,
  Settings,
  ShieldCheck,
  Search,
  Bell,
  Globe,
  LogOut,
  User as UserIcon,
  Inbox,
  Megaphone,
  HelpCircle,
  Sparkles,
  Eye,
  EyeOff,
  Trophy,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore, useEffectiveRole } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import {
  useNotifications,
  useMarkNotificationRead,
  useAnnouncementUnreadCount,
  useMe,
} from '@/lib/queries';
import { useState, useRef, useEffect } from 'react';
import type { Role } from '@isoft-people/types';
import { WelcomeWizard, openWelcomeWizard } from './WelcomeWizard';
import { IsoftLockup, IsoftIsotipo } from './Logo';

type NavItem = {
  to: string;
  label: string;
  icon: any;
  exact?: boolean;
  roles: Role[];
};

const ALL_NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: Home, exact: true, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/colaboradores', label: 'Colaboradores', icon: Users, roles: ['ADMIN', 'HR', 'MANAGER'] },
  { to: '/organigrama', label: 'Organigrama', icon: Network, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/permisos', label: 'Permisos y Vacaciones', icon: CalendarClock, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/bandeja', label: 'Bandeja de aprobaciones', icon: Inbox, roles: ['ADMIN', 'HR', 'MANAGER'] },
  { to: '/calendario', label: 'Calendario', icon: Calendar, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/anuncios', label: 'Anuncios', icon: Megaphone, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/mundial', label: 'Mundial 2026', icon: Trophy, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
  { to: '/configuracion', label: 'Configuración', icon: Settings, roles: ['ADMIN', 'HR'] },
  { to: '/auditoria', label: 'Auditoría', icon: ShieldCheck, roles: ['ADMIN'] },
  { to: '/ayuda', label: 'Ayuda', icon: HelpCircle, roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'] },
];

/** Items del bottom nav móvil (estilo app instalada). El resto vive en el menú. */
const MOBILE_NAV_PATHS = ['/', '/permisos', '/anuncios', '/mundial'];

function NavLinks({
  items,
  unreadAnnouncements,
  onNavigate,
}: {
  items: NavItem[];
  unreadAnnouncements: number;
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.exact}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex h-10 flex-shrink-0 items-center gap-3 rounded-md px-3 text-sm transition ${
              isActive
                ? 'bg-primary font-semibold text-white shadow-sm'
                : 'font-medium text-white/60 hover:bg-white/5 hover:text-white'
            }`
          }
        >
          <item.icon size={18} strokeWidth={2} />
          <span className="flex-1 truncate">{item.label}</span>
          {item.to === '/anuncios' && unreadAnnouncements > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-error px-1 text-[10px] font-semibold text-white">
              {unreadAnnouncements}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function DashboardLayout() {
  const user = useAuthStore((s) => s.user);
  const realRole = (user?.role ?? 'EMPLOYEE') as Role;
  const role: Role = (useEffectiveRole() ?? 'EMPLOYEE') as Role;
  const viewAsCollaborator = useAuthStore((s) => s.viewAsCollaborator);
  const setViewAsCollaborator = useAuthStore((s) => s.setViewAsCollaborator);
  const canSwitchView = realRole === 'ADMIN' || realRole === 'HR' || realRole === 'MANAGER';
  const navItems = ALL_NAV.filter((n) => n.roles.includes(role));
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { data: notifs } = useNotifications();
  const { data: unreadAnn } = useAnnouncementUnreadCount();
  const { data: me } = useMe();
  const countryCode: string | undefined = me?.employee?.country?.code;
  const markRead = useMarkNotificationRead();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const unreadCount = unreadAnn?.count ?? 0;
  const mobileNav = navItems.filter((n) => MOBILE_NAV_PATHS.includes(n.to));

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Bloquear scroll del body cuando el drawer móvil está abierto
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Tour de bienvenida (auto en el primer ingreso + reabrible) */}
      <WelcomeWizard />

      {/* Sidebar — sólo desktop */}
      <aside className="hidden w-60 flex-shrink-0 flex-col bg-bg-sidebar text-white lg:flex">
        <div className="flex h-16 items-center border-b border-white/5 px-5">
          <IsoftLockup onDark />
        </div>
        <NavLinks items={navItems} unreadAnnouncements={unreadCount} />
        <div className="border-t border-white/5 p-4 text-[11px] text-white/40">
          v1.0 · ISOFT © 2026
        </div>
      </aside>

      {/* Drawer móvil */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div
            className="absolute inset-0 bg-black/55 anim-fade-in"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[82vw] flex-col bg-bg-sidebar text-white shadow-2xl pt-safe">
            <div className="flex h-16 flex-shrink-0 items-center justify-between border-b border-white/5 px-5">
              <IsoftLockup onDark />
              <button
                onClick={() => setMobileNavOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-white/60 hover:bg-white/10 hover:text-white"
                aria-label="Cerrar menú"
              >
                <X size={20} />
              </button>
            </div>
            <NavLinks
              items={navItems}
              unreadAnnouncements={unreadCount}
              onNavigate={() => setMobileNavOpen(false)}
            />
            <div
              className="border-t border-white/5 p-4 text-[11px] text-white/40"
              style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
            >
              v1.0 · ISOFT © 2026
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* pt-safe: compensa el Dynamic Island/notch en PWA iOS (viewport-fit=cover) */}
        <header className="flex-shrink-0 border-b border-border bg-white pt-safe">
        <div className="flex h-16 items-center justify-between gap-3 px-4 lg:px-8">
          {/* Izquierda: hamburguesa + logo (móvil) / búsqueda (desktop) */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md text-font-secondary hover:bg-bg lg:hidden"
              aria-label="Abrir menú"
            >
              <Menu size={22} />
            </button>
            <span className="lg:hidden">
              <IsoftIsotipo className="h-8 w-8" />
            </span>
            <div className="hidden h-10 w-full max-w-[380px] items-center gap-2 rounded-md border border-border bg-bg px-3 md:flex">
              <Search size={16} className="text-font-tertiary" />
              <input
                placeholder="Buscar colaborador, permiso..."
                className="flex-1 bg-transparent text-sm placeholder:text-font-tertiary focus:outline-none"
              />
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2 sm:gap-4">
            {countryCode && (
              <span
                title="Tu país"
                className="hidden h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-font-secondary sm:flex"
              >
                <Globe size={14} className="text-font-tertiary" />
                {countryCode}
              </span>
            )}

            <div className="relative">
              <button
                onClick={() => setShowNotifs((v) => !v)}
                className="relative flex h-9 w-9 items-center justify-center rounded-md hover:bg-bg"
              >
                <Bell size={18} className="text-font-secondary" />
                {notifs && notifs.unreadCount > 0 && (
                  <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-error text-[10px] font-semibold text-white">
                    {notifs.unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 top-11 z-50 max-h-[70vh] w-[calc(100vw-2rem)] max-w-96 overflow-y-auto rounded-lg border border-border bg-white shadow-xl">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <h3 className="text-sm font-semibold">Notificaciones</h3>
                    <button
                      onClick={() => {
                        setShowNotifs(false);
                        navigate('/notificaciones');
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Ver todas
                    </button>
                  </div>
                  {notifs?.items.length === 0 && (
                    <p className="p-6 text-center text-xs text-font-secondary">
                      Sin notificaciones
                    </p>
                  )}
                  {notifs?.items.slice(0, 6).map((n: any) => (
                    <button
                      key={n.id}
                      onClick={() => {
                        if (!n.readAt) markRead.mutate(n.id);
                        if (n.link) navigate(n.link);
                        setShowNotifs(false);
                      }}
                      className={`flex w-full items-start gap-3 border-b border-border-light p-3 text-left hover:bg-bg ${
                        !n.readAt ? 'bg-primary-subtle/30' : ''
                      }`}
                    >
                      <div className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-font-primary">{n.title}</p>
                        <p className="mt-0.5 text-xs text-font-secondary line-clamp-2">
                          {n.body}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-xs font-semibold text-white"
              >
                {user ? initials(user.fullName ?? user.email) : 'AD'}
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-11 z-50 w-56 rounded-lg border border-border bg-white p-1 shadow-xl">
                  <div className="border-b border-border px-3 py-2">
                    <p className="text-sm font-semibold text-font-primary">
                      {user?.fullName ?? 'Admin'}
                    </p>
                    <p className="truncate text-xs text-font-secondary">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/mi-perfil');
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-font-primary hover:bg-bg"
                  >
                    <UserIcon size={14} /> Mi perfil
                  </button>
                  {canSwitchView && (
                    <button
                      onClick={() => {
                        setViewAsCollaborator(!viewAsCollaborator);
                        setShowUserMenu(false);
                        navigate('/');
                      }}
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-font-primary hover:bg-bg"
                    >
                      {viewAsCollaborator ? (
                        <>
                          <EyeOff size={14} className="text-primary" /> Salir de vista colaborador
                        </>
                      ) : (
                        <>
                          <Eye size={14} className="text-primary" /> Vista colaborador
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      openWelcomeWizard();
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-font-primary hover:bg-bg"
                  >
                    <Sparkles size={14} className="text-primary" /> Ver tutorial
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/ayuda');
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-font-primary hover:bg-bg"
                  >
                    <HelpCircle size={14} className="text-primary" /> Centro de ayuda
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-error hover:bg-red-50"
                  >
                    <LogOut size={14} /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </header>

        {viewAsCollaborator && canSwitchView && (
          <div className="flex items-center justify-center gap-2 bg-primary px-4 py-1.5 text-xs font-semibold text-white">
            <Eye size={13} /> Estás viendo la plataforma como colaborador
            <button
              onClick={() => setViewAsCollaborator(false)}
              className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold transition hover:bg-white/30"
            >
              Salir
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8">
          <Outlet />
        </main>

        {/* Bottom nav — sólo móvil, estilo app instalada */}
        <nav
          className="fixed inset-x-0 bottom-0 z-[90] flex border-t border-border bg-white/95 backdrop-blur lg:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {mobileNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold transition ${
                  isActive ? 'text-primary' : 'text-font-tertiary'
                }`
              }
            >
              <item.icon size={20} strokeWidth={2} />
              <span className="truncate">{item.label.split(' ')[0]}</span>
              {item.to === '/anuncios' && unreadCount > 0 && (
                <span className="absolute left-1/2 top-1 ml-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error px-1 text-[9px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </NavLink>
          ))}
          <button
            onClick={() => setMobileNavOpen(true)}
            className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold text-font-tertiary"
          >
            <Menu size={20} strokeWidth={2} />
            <span>Menú</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
